import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Auth0User } from '../models/auth0User';
import { User, Role } from '../models/user/user';
import { InviteUser } from '../models/user/invite-user';
import { BaseService } from './base.service';
import { ApiVersions } from './api-version';

@Injectable()
export class Auth0Service extends BaseService {
  private basePath = 'auth0/users';

  public usersChanged = new Subject<User[]>();
  public usersIncludingStaffChanged = new Subject<User[]>();
  public userinvited = new Subject<void>();
  public userChanged = new Subject<void>();
  public userSelected = new Subject<User>();
  private _users: User[];
  private _usersIncludingStaff: User[];
  private _saving = false;

  public get users(): User[] {
    return this._users;
  }

  public get usersIncludingStaff(): User[] {
    return this._usersIncludingStaff;
  }
  constructor() {
    super();
  }

  changePassword() {
    return this.post(this.getPath(`changePassword`), {});
  }

  getBySecureLinkId(id: string): Observable<Auth0User> {
    return this.get(`${this.basePath}/bySecureLink/${id}`, true, ApiVersions.v0_1, true).pipe(
      map(Auth0User.parse),
      catchError((error) => {
        if (error?.status === 400) {
          this.router.navigate(['/create-account/expired']);
        } else {
          if (error?.status !== 404) {
            console.error('unexpected error response', error);
          }
          this.router.navigate(['/create-account/closed']);
        }
        return EMPTY;
      })
    );
  }

  setPassword(id: string, auth0User: Auth0User): Observable<Object> {
    let path = `${this.basePath}/bySecureLink/${id}/setPassword`;
    return this.post(path, auth0User).pipe(
      map((data) => {
        return data;
      })
    );
  }

  getAllUsers() {
    this.get<User[]>(this.basePath, true)
      .pipe(
        map((users) => {
          if (users) {
            return users.map((user) => {
              return new User(user);
            });
          }
        })
      )
      .subscribe((users) => {
        if (users) {
          this._usersIncludingStaff = users;
          this._users = users.filter((u) => u.role !== Role.Staff);

          this.usersIncludingStaffChanged.next(this.usersIncludingStaff);
          this.usersChanged.next(this.users);
        }
      });
  }
  getUser(userId: string) {
    if (this._users) {
      this._findUser(userId);
    } else {
      this.usersChanged.subscribe(() => {
        this._findUser(userId);
      });
    }
  }

  resendInvitation(email: string) {
    return this.post(`${this.basePath}/${encodeURIComponent(email)}/resendInvitation`, {});
  }

  private _findUser(userId: string) {
    let selectedUser = this._users.find((x) => x.userId == userId);
    this.userSelected.next(selectedUser);
  }

  inviteNewUser(newUser: InviteUser) {
    if (this._saving) return;
    this._saving = true;
    let body = JSON.stringify(newUser);
    this.post(this.basePath, body).subscribe(
      (result) => {
        this._saving = false;
        if (result) {
          if (result.success == true) {
            this._users.push(new User(result.data));
            this.usersChanged.next(this._users);
            this.userinvited.next();
            this.openSnackBar(`Successfully invited ${newUser.email}`, true);
          } else {
            this.openSnackBar(result.errorMessages, false);
          }
        }
      },
      (err) => {
        this._saving = false;
      }
    );
  }

  createCurrentUser(): Observable<void> {
    return this.post(this.basePath, null);
  }

  updateUserRole(email: string, role: string): Observable<any> {
    if (this._saving) return;
    this._saving = true;
    let obs = this.put(`${this.basePath}/${email}/roles?type=${role}`);
    obs.subscribe(
      (result) => {
        this._saving = false;
        if (result) {
          if (result.success == true) {
            var index = this._users.findIndex((x) => x.userId == result.data.userId);
            this._users[index] = new User(result.data);
            this.usersChanged.next(this._users);
            this.userChanged.next();
            this.openSnackBar(`Successfully updated ${email}`, true);
          } else {
            this.openSnackBar(result.errorMessages, false);
          }
        }
      },
      (err) => {
        this._saving = false;
      }
    );
    return obs;
  }

  deleteUser(user: User) {
    if (this._saving) return;
    this._saving = true;

    this.delete(`${this.basePath}/${user.userId}`).subscribe(
      (result) => {
        this._saving = false;
        if (result) {
          if (result.success == true) {
            var index = this._users.indexOf(user);
            if (index !== -1) {
              this._users.splice(index, 1);
              this.usersChanged.next(this._users);
              this.userChanged.next();
              this.openSnackBar(`Successfully revoked ${user.displayName} access`, true);
            }
          } else {
            this.openSnackBar(result.errorMessages, false);
          }
        }
      },
      (err) => {
        this._saving = false;
      }
    );
  }
  private getPath(path) {
    return `${this.basePath}/${path}`;
  }
}
